import "./FileDropzone.css";
import { EXCEL_CONFIG, MESSAGES } from "../../utils/constants.js";

class FileDropzone {
  constructor() {
    this.onFileSelect = null; // Callback quand un fichier est sélectionné
    this.onError = null; // Callback pour les erreurs
    this.currentFile = null;
    this.isProcessing = false;
  }

  setFileSelectHandler(callback) {
    this.onFileSelect = callback;
  }

  setErrorHandler(callback) {
    this.onError = callback;
  }

  mount(selector) {
    const container = document.querySelector(selector);
    if (!container) {
      throw new Error(`Container not found: ${selector}`);
    }

    container.innerHTML = `
      <div class="file-dropzone">
        <div id="drop-area" class="upload-area visible">
          <div class="upload-info">
            <div class="upload-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                <polyline points="17,8 12,3 7,8"></polyline>
                <line x1="12" y1="3" x2="12" y2="15"></line>
              </svg>
            </div>
            <h3 class="upload-title">Glissez votre fichier Excel ici</h3>
            <p class="upload-description">ou cliquez pour sélectionner un fichier</p>
            <div class="upload-requirements">
              <small>Fichiers acceptés : ${EXCEL_CONFIG.acceptedTypes.join(", ")}</small>
              <small>Taille maximum : ${Math.round(EXCEL_CONFIG.maxFileSize / 1024 / 1024)} Mo</small>
            </div>
          </div>
        </div>
        
        <input type="file" id="file-input" accept="${EXCEL_CONFIG.acceptedTypes.join(",")}" style="display: none;">
        
        <div id="file-info" class="file-info hidden">
          <div class="file-details">
            <div class="file-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14,2 14,8 20,8"></polyline>
              </svg>
            </div>
            <div class="file-meta">
              <div class="file-name"></div>
              <div class="file-size"></div>
            </div>
            <button id="remove-file" class="btn-remove" title="Supprimer le fichier">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  bindEvents() {
    const dropArea = document.getElementById("drop-area");
    const fileInput = document.getElementById("file-input");
    const removeBtn = document.getElementById("remove-file");

    // Drag & Drop events
    dropArea.addEventListener("dragover", (e) => {
      e.preventDefault();
      if (!this.isProcessing) {
        dropArea.classList.add("drag-over");
      }
    });

    dropArea.addEventListener("dragleave", (e) => {
      e.preventDefault();
      if (!dropArea.contains(e.relatedTarget)) {
        dropArea.classList.remove("drag-over");
      }
    });

    dropArea.addEventListener("drop", (e) => {
      e.preventDefault();
      dropArea.classList.remove("drag-over");

      if (this.isProcessing) return;

      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.handleFile(files[0]);
      }
    });

    // Click to select
    dropArea.addEventListener("click", () => {
      if (!this.isProcessing) {
        fileInput.click();
      }
    });

    fileInput.addEventListener("change", (e) => {
      if (e.target.files.length > 0) {
        this.handleFile(e.target.files[0]);
      }
    });

    // Remove file
    removeBtn.addEventListener("click", (e) => {
      e.stopPropagation();
      this.removeFile();
    });
  }

  handleFile(file) {
    // Validation du type
    const fileExtension = "." + file.name.split(".").pop().toLowerCase();
    if (!EXCEL_CONFIG.acceptedTypes.includes(fileExtension)) {
      this.showError(MESSAGES.UPLOAD_ERROR_TYPE);
      return;
    }

    // Validation de la taille
    if (file.size > EXCEL_CONFIG.maxFileSize) {
      this.showError(MESSAGES.UPLOAD_ERROR_SIZE);
      return;
    }

    this.currentFile = file;
    this.showFileInfo(file);

    // Déclencher le callback
    if (this.onFileSelect) {
      this.onFileSelect(file);
    }
  }

  showFileInfo(file) {
    const fileInfo = document.getElementById("file-info");
    const dropArea = document.getElementById("drop-area");
    const fileName = fileInfo.querySelector(".file-name");
    const fileSize = fileInfo.querySelector(".file-size");

    fileName.textContent = file.name;
    fileSize.textContent = this.formatFileSize(file.size);

    dropArea.classList.add("hidden");
    dropArea.classList.remove("visible");
    fileInfo.classList.remove("hidden");
    fileInfo.classList.add("visible");
  }

  removeFile() {
    this.currentFile = null;
    const fileInfo = document.getElementById("file-info");
    const dropArea = document.getElementById("drop-area");
    const fileInput = document.getElementById("file-input");

    fileInput.value = "";
    dropArea.classList.remove("hidden");
    dropArea.classList.add("visible");
    fileInfo.classList.add("hidden");
    fileInfo.classList.remove("visible");

    this.clearStates();
  }

  setProcessing(processing) {
    this.isProcessing = processing;
    const dropArea = document.getElementById("drop-area");

    if (processing) {
      dropArea.classList.add("loading");
      dropArea.classList.remove("error");
    } else {
      dropArea.classList.remove("loading");
    }
  }

  showError(message) {
    const dropArea = document.getElementById("drop-area");
    dropArea.classList.add("error");
    dropArea.classList.remove("loading");

    if (this.onError) {
      this.onError(message);
    }

    // Retirer l'état d'erreur après 3 secondes
    setTimeout(() => {
      dropArea.classList.remove("error");
    }, 3000);
  }

  clearStates() {
    const dropArea = document.getElementById("drop-area");
    dropArea.classList.remove("drag-over", "loading", "error");
    this.isProcessing = false;
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  getCurrentFile() {
    return this.currentFile;
  }

  hasFile() {
    return this.currentFile !== null;
  }
}

export default FileDropzone;
